import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function QRBarcodePrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="QR & Barcode Scanner: Fast Reader Pro"
      date="NOVEMBER 2025"
    >
      {/* Intro */}
      <p>
        This Privacy Policy describes how QR &amp; Barcode Scanner: Fast Reader
        Pro (“we”, “our”, “us”, or “the Application”) collects, uses, and
        protects information when you use our mobile application. By
        downloading or using the Application, you agree to this Policy.
      </p>

      {/* 1. Information We Collect */}
      <div className={s.title}>1) Information We Collect</div>

      <p><strong>Camera and Gallery Access.</strong></p>
      <ul>
        <li>The Application uses your device’s camera to scan QR codes and barcodes.</li>
        <li>
          If you use the “Scan from Gallery” PRO feature, the Application
          requires access to photos to read codes from images.
        </li>
        <li>These scans are processed locally and may be stored in your scan history.</li>
      </ul>

      <p><strong>Scan History.</strong></p>
      <ul>
        <li>The Application automatically saves your scanned codes in history.</li>
        <li>Free version: up to 10 records.</li>
        <li>PRO version: unlimited history.</li>
        <li>
          Data is stored securely in Firebase Firestore and linked only to your
          device.
        </li>
      </ul>

      <p><strong>User-Provided Data.</strong></p>
      <ul>
        <li>No registration or personal account is required.</li>
        <li>
          We do not collect names, emails, or personal identifiers unless
          voluntarily provided via support requests.
        </li>
      </ul>

      <p><strong>Automatically Collected Data.</strong></p>
      <ul>
        <li>Device information (model, operating system, language).</li>
        <li>Usage data (features used, scans performed, session duration).</li>
        <li>Crash reports and performance metrics (via Firebase Crashlytics).</li>
      </ul>

      <p><strong>Payment Data.</strong></p>
      <ul>
        <li>
          Subscription purchases (weekly, monthly, annual PRO) are processed by
          Apple App Store and Google Play.
        </li>
        <li>We do not store or access your payment card details.</li>
      </ul>

      <p><strong>Third-Party Services.</strong></p>
      <ul>
        <li>Firebase Analytics &amp; Crashlytics – usage analytics and crash reporting.</li>
        <li>RevenueCat – subscription management and paywall handling.</li>
      </ul>
      <p>
        These providers may collect limited technical data under their own
        privacy policies.
      </p>

      {/* 2. How We Use Information */}
      <div className={s.title}>2) How We Use Information</div>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide QR and barcode scanning services.</li>
        <li>Store and manage scan history.</li>
        <li>Enable PRO features (QR generation, scan from gallery).</li>
        <li>Improve app performance and fix technical issues.</li>
        <li>Manage and verify subscriptions.</li>
        <li>Comply with applicable laws.</li>
      </ul>

      {/* 3. Legal Basis for Processing (GDPR) */}
      <div className={s.title}>3) Legal Basis for Processing (GDPR)</div>
      <p>For EEA users, data is processed under:</p>
      <ul>
        <li>
          <strong>Performance of a contract</strong> – to provide scanning and
          history features.
        </li>
        <li>
          <strong>Consent</strong> – for optional analytics and third-party tools.
        </li>
        <li>
          <strong>Legitimate interest</strong> – to improve services and prevent
          abuse.
        </li>
        <li>
          <strong>Legal obligation</strong> – where required by law.
        </li>
      </ul>

      {/* 4. Data Retention */}
      <div className={s.title}>4) Data Retention</div>
      <ul>
        <li>Scan history is stored until you delete it manually or uninstall the app.</li>
        <li>
          Technical and analytics data is retained only as long as necessary for
          improvements.
        </li>
        <li>Subscription data is managed directly by App Store and Google Play.</li>
      </ul>

      {/* 5. Sharing and Disclosure */}
      <div className={s.title}>5) Sharing and Disclosure</div>
      <p>We do not sell or rent personal data. Data may be shared only:</p>
      <ul>
        <li>With trusted third-party providers (Firebase, RevenueCat).</li>
        <li>If required by law, court order, or government request.</li>
        <li>
          In case of business transfer (e.g., merger, acquisition), subject to
          appropriate safeguards.
        </li>
      </ul>

      {/* 6. International Data Transfers */}
      <div className={s.title}>6) International Data Transfers</div>
      <p>
        Your data may be transferred internationally. We ensure compliance with
        GDPR safeguards, such as Standard Contractual Clauses.
      </p>

      {/* 7. User Rights */}
      <div className={s.title}>7) User Rights</div>

      <p><strong>Under GDPR (EEA users):</strong></p>
      <ul>
        <li>Right to access, correct, or delete your data.</li>
        <li>Right to restrict or object to processing.</li>
        <li>Right to withdraw consent.</li>
        <li>Right to data portability.</li>
        <li>Right to file a complaint with a Data Protection Authority.</li>
      </ul>

      <p><strong>Under CCPA (California users):</strong></p>
      <ul>
        <li>Right to know what data we collect.</li>
        <li>Right to request deletion of your data.</li>
        <li>
          Right to opt out of data sales (we do not sell personal data).
        </li>
        <li>
          Right to non-discrimination when exercising privacy rights.
        </li>
      </ul>

      <p>Requests can be made via our contact email below.</p>

      {/* 8. Children’s Privacy */}
      <div className={s.title}>8) Children’s Privacy</div>
      <p>
        The Application is not directed to children under 13 (or 16 in some
        regions). We do not knowingly collect personal information from
        children.
      </p>

      {/* 9. Data Security */}
      <div className={s.title}>9) Data Security</div>
      <p>
        We apply industry-standard measures, including encryption, secure
        storage, and restricted access, to protect your data. However, no
        system can guarantee 100% security.
      </p>

      {/* 10. Changes to This Privacy Policy */}
      <div className={s.title}>10) Changes to This Privacy Policy</div>
      <p>
        We may update this Privacy Policy from time to time. Any changes will be
        published in the Application or on the store page. Continued use of the
        Application after updates means acceptance of the new Policy.
      </p>

      {/* 11. Contact Us */}
      <div className={s.title}>11) Contact Us</div>
      <p>For questions or privacy-related requests, please contact:</p>
      <p>Developer: Aleksandra Rakitina</p>
      <p>
        Email:{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a>
      </p>
    </PolicyLayout>
  );
}
